import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { isNil } from 'lodash';
import { AiAdjustmentService } from 'src/api/activity/ai-adjustment/ai-adjustment.service';
import {
  IAiAdjustmentAvailabilityResponse,
  IAiAdjustmentQuota,
  TAiAdjustmentBlockedReason,
} from 'typings/api-activity';

/**
 * What the AI dock needs to know before it renders anything.
 *
 * `visible` is separate from `allowed` because the two blocked reasons behave
 * differently: a spent budget still shows the dock, disabled, with the reset
 * date, while a disabled feature flag shows nothing at all.
 */
export interface IAiAdjustmentAvailability {
  /** True when an adjustment request would pass every check */
  allowed: boolean;
  /** True when the AI UI should be rendered at all, enabled or not */
  visible: boolean;
  /** Why the adjustment is refused, null when allowed */
  reason: TAiAdjustmentBlockedReason | null;
  /** The customer's stored vision text, null when none is stored */
  organizationVision: string | null;
  /** The quota window, null when AI is not enabled at all */
  quota: IAiAdjustmentQuota | null;
  /** Share of the budget still available, as an integer percentage, or null without a quota window */
  budgetRemaining: number | null;
}

/** Nothing available, nothing rendered. The state every failure falls back to. */
const UNAVAILABLE: IAiAdjustmentAvailability = {
  allowed: false,
  visible: false,
  reason: null,
  organizationVision: null,
  quota: null,
  budgetRemaining: null,
};

/**
 * Reasons that hide the AI UI completely rather than disabling it.
 *
 * The two enablement flags mean "no AI UI at all" per ES-2373 §1. An archived
 * activity is not editable anyway, so the dock has nothing to offer there
 * either.
 */
const REASONS_THAT_HIDE_THE_UI: TAiAdjustmentBlockedReason[] = [
  'ai_not_enabled_for_user',
  'ai_not_enabled_for_organization',
  'activity_archived',
];

/**
 * Translates the availability response into what the dock renders.
 *
 * Pure and exported for testing: this is the whole gating rule in one place.
 *
 * @param response The availability response, or null when the call failed
 * @returns What the dock should render
 */
export function toAvailability(response: IAiAdjustmentAvailabilityResponse | null): IAiAdjustmentAvailability {
  if (isNil(response)) {
    return UNAVAILABLE;
  }

  const reason = response.reason ?? null;
  const quota = response.quota ?? null;

  return {
    allowed: response.allowed === true,
    visible: isNil(reason) || !REASONS_THAT_HIDE_THE_UI.includes(reason),
    reason,
    organizationVision: response.organization_vision ?? null,
    quota,
    // The API reports the share SPENT, rounded up, so the remaining share
    // rounds down. That is the conservative direction: it never promises
    // budget that is not there.
    //
    budgetRemaining: isNil(quota) ? null : Math.max(0, 100 - quota.quota_used),
  };
}

/**
 * Holds AI adjustment availability for the activity currently being edited.
 *
 * Availability is per (organization unit, activity) and changes whenever an
 * adjustment is spent or a vision is saved, so it is refetched on demand
 * rather than cached indefinitely. A single subject is enough: only one
 * activity is open at a time.
 */
@Injectable({
  providedIn: 'root',
})
export class AiAdjustmentProvider {
  private availabilitySubject$ = new BehaviorSubject<IAiAdjustmentAvailability>(UNAVAILABLE);

  /** What the dock should render right now */
  public availability$: Observable<IAiAdjustmentAvailability> = this.availabilitySubject$.asObservable();

  /** True when the AI UI should be rendered at all */
  public isVisible$: Observable<boolean> = this.availability$.pipe(map((availability) => availability.visible));

  /** True when an adjustment request would be accepted */
  public isAllowed$: Observable<boolean> = this.availability$.pipe(map((availability) => availability.allowed));

  /** The last known state, for callers that cannot wait for the observable */
  public get availability(): IAiAdjustmentAvailability {
    return this.availabilitySubject$.value;
  }

  private lastRequest: { organizationUnitId: number; activityId: number } | null = null;

  /** Bumped per request, so only the newest one may publish */
  private requestSequence = 0;

  constructor(private aiAdjustmentService: AiAdjustmentService) {}

  /**
   * Fetches availability for an activity and publishes the result.
   *
   * A failure resolves to the hidden state rather than throwing: the AI dock is
   * an optional affordance, and an availability check that cannot be completed
   * must never break the activity editor around it. That also covers the 403
   * the API throws for a user without write access, which is a legitimate
   * "no AI here" answer rather than an error worth showing.
   *
   * @param organizationUnitId The organization unit the user is working in
   * @param activityId The activity being edited
   */
  public async refresh(organizationUnitId: number, activityId: number): Promise<IAiAdjustmentAvailability> {
    const sequence = ++this.requestSequence;

    if (isNil(organizationUnitId) || isNil(activityId)) {
      this.lastRequest = null;
      this.availabilitySubject$.next(UNAVAILABLE);
      return UNAVAILABLE;
    }

    this.lastRequest = { organizationUnitId, activityId };

    let availability: IAiAdjustmentAvailability;

    try {
      availability = toAvailability(await this.aiAdjustmentService.availability(organizationUnitId, activityId));
    } catch {
      availability = UNAVAILABLE;
    }

    // Callers do not await this, and the editor re-enters it on every language
    // switch and copy, so two requests can be in flight at once. An older
    // answer arriving last would leave the dock describing an activity that is
    // no longer open — and since an archived activity HIDES the dock, that
    // shows up as a missing dock on a perfectly editable one.
    //
    if (sequence !== this.requestSequence) {
      return availability;
    }

    this.availabilitySubject$.next(availability);

    return availability;
  }

  /**
   * Refetches availability for whatever was last asked about.
   *
   * Used after an adjustment is spent (the budget moved) and after a vision is
   * saved (the vision toggle can now be offered).
   */
  public async reload(): Promise<void> {
    if (isNil(this.lastRequest)) {
      return;
    }

    await this.refresh(this.lastRequest.organizationUnitId, this.lastRequest.activityId);
  }

  /**
   * Drops the current state, for when the editor closes.
   *
   * Bumps the sequence too: this provider is `providedIn: 'root'`, so a
   * response still in flight would otherwise repopulate the state the editor
   * just dropped, and the next activity opened would flash the previous one's
   * dock before its own check lands.
   */
  public clear(): void {
    this.requestSequence++;
    this.lastRequest = null;
    this.availabilitySubject$.next(UNAVAILABLE);
  }
}
